import { z } from 'zod';

/** Primitive riusate da tutti gli schema di entità. */

export const idSchema = z.coerce.number().int().positive();
export type Id = z.infer<typeof idSchema>;

/**
 * Id di una scelta da elenco (tendina), con messaggio parlante.
 *
 * Sotto è pur sempre un id numerico, ma l'utente vede un selettore: se non
 * sceglie, "Inserisci un numero valido" (dall'error map globale) è
 * incomprensibile. Qui il messaggio copre TUTTI i modi in cui la scelta può
 * mancare o essere invalida — perciò va passato a ogni check, dato che l'errore
 * del solo `number` non si propaga a `.int()/.positive()`.
 *
 * @example ruoloId: idSelezioneSchema('Seleziona un ruolo')
 */
export const idSelezioneSchema = (messaggio: string) =>
  z.coerce.number({ error: messaggio }).int(messaggio).positive(messaggio);

/** Codice fiscale italiano: 16 caratteri (persona fisica) o 11 cifre (soggetto giuridico). */
export const codiceFiscaleSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => /^[A-Z0-9]{16}$/.test(v) || /^\d{11}$/.test(v), {
    message: 'Codice fiscale non valido (16 caratteri alfanumerici oppure 11 cifre)',
  });

/**
 * Payload di correzione riservata del codice fiscale (clienti/partner):
 * azione distinta dall'update standard, con motivazione obbligatoria —
 * si traccia in `audit_log`, non solo con autore/timestamp come gli altri campi.
 */
export const correzioneCodiceFiscaleSchema = z.object({
  codiceFiscale: codiceFiscaleSchema,
  motivazione: z.string().trim().min(10, 'La motivazione deve avere almeno 10 caratteri'),
});
export type CorrezioneCodiceFiscale = z.infer<typeof correzioneCodiceFiscaleSchema>;

export const partitaIvaSchema = z
  .string()
  .trim()
  .regex(/^\d{11}$/, 'La partita IVA deve essere di 11 cifre');

export const codiceAtecoSchema = z
  .string()
  .trim()
  .regex(/^\d{2}(\.\d{1,2}){0,2}$/, 'Codice ATECO non valido');

// Il messaggio è quello che l'utente legge sotto al campo: deve stare nella
// lingua del prodotto, non nel default inglese di Zod.
export const emailSchema = z.email('Indirizzo email non valido').trim().toLowerCase();

export const telefonoSchema = z
  .string()
  .trim()
  .min(6)
  .max(32)
  .regex(/^[+0-9 ().\-/]+$/, 'Numero di telefono non valido');

/** Codice destinatario SDI: 7 caratteri alfanumerici. */
export const codiceSdiSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{7}$/, 'Codice destinatario SDI non valido');

export const capSchema = z
  .string()
  .trim()
  .regex(/^\d{5}$/, 'CAP non valido');

/** Importo in euro: memorizzato come numeric(14,2), trasportato come numero. */
export const importoSchema = z.coerce.number().min(0).max(9_999_999_999.99);

/** Data senza orario, in formato ISO `YYYY-MM-DD`. */
export const dataSchema = z.iso.date();

/** Istante completo ISO-8601. */
export const dataOraSchema = z.iso.datetime({ offset: true });

/** Parametri di paginazione comuni a tutte le liste. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().trim().optional(),
  order: z.enum(['asc', 'desc']).default('asc'),
  q: z.string().trim().min(1).optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** Tetto rigido dell'export CSV "base" (azione diretta, non il job in background). */
export const EXPORT_CSV_MAX_ROWS = 5000;

/** Envelope delle risposte paginate. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Colonne di tracciamento "chi/quando" presenti sulle entità tracciate. */
export const auditFieldsSchema = z.object({
  creatoDa: idSchema.nullable(),
  creatoIl: dataOraSchema,
  modificatoDa: idSchema.nullable(),
  modificatoIl: dataOraSchema.nullable(),
});
export type AuditFields = z.infer<typeof auditFieldsSchema>;

/**
 * Forma dell'errore restituito dall'API (filtro eccezioni di `apps/api`).
 * Schema Zod così da poterlo documentare in OpenAPI come corpo delle risposte
 * di errore (400/401/…); il tipo resta inferito, fonte unica di verità.
 */
export const apiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: z.string(),
  message: z.string(),
  /** Errori di validazione per campo, nel formato `path` → messaggi. */
  fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
  /**
   * Dettagli diagnostici (messaggio reale, stack) dei 5xx. Popolato SOLO fuori
   * produzione: in produzione i dettagli interni restano nei log del server.
   */
  debug: z
    .object({
      name: z.string().optional(),
      message: z.string().optional(),
      stack: z.string().optional(),
    })
    .optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
